import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Card from '@src/components/Card';
import { Box, CircularProgress, Divider, useMediaQuery } from '@mui/material';
import Table from '@src/new-components/Table';
import { useTranslation } from 'react-i18next';
import apis from '@src/apis';
import moment from 'moment';
import i18n from '@src/languages';
import Typography from '@src/new-components/Typography';
import { delimitNumber } from '@src/utils/number';
import { PAGINATION_LIMIT } from '@src/constants';
import { COLOR } from '@src/styles/color';
import {
  VOICE_REVENUE_TRANSACTION_STATUS,
  VOICE_REVENUE_TRANSACTION_STATUS_BACKGROUND_COLORS,
  VOICE_REVENUE_TRANSACTION_STATUS_TEXT_COLORS,
} from '@src/constants/voiceCloning';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import {
  VOICE_REVENUE_TRANSACTION_IN_VALID,
  WITHDRAWAL_REQUEST_SORT,
} from '@src/constants/withdrawal';
import Withdrawal from '@src/containers/Withdrawal';
import theme from '@src/styles/theme';
import ROUTES from '@src/constants/route';
import { TRANSACTION_HISTORY_TABS } from '@src/constants/transactionHistory';
import { getOrdinal } from '@src/utils/string';
import { StyledChip, StyledTransactionsHistory } from './index.style';
import TransactionHistoryHeader from './TransactionHistoryHeader';
import CancelWithdrawalRequestDialog from './CancelWithdrawalRequestDialog';
import EmptyWithdrawalRequest from './EmptyWithdrawalRequest';
import TransactionHistoryTabs from './TransactionHistoryTabs';

const TABS = [
  {
    label: 'withdrawal',
    name: TRANSACTION_HISTORY_TABS.WITHDRAWAL,
  },
  {
    label: 'claimRewards',
    name: TRANSACTION_HISTORY_TABS.REWARD_TRANSACTION,
  },
];
const TransactionsHistory = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryParams = new URLSearchParams(location.search);
  const transactionId = queryParams.get('id');
  const [activeTab, setActiveTab] = useState(
    TRANSACTION_HISTORY_TABS.WITHDRAWAL,
  );
  const [historyRequests, setHistoryRequests] = useState([]);
  const [paging, setPaging] = useState({ page: 0, total: 0 });
  const { language } = i18n;
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_LIMIT);
  const dispatch = useDispatch();

  const [isOpenPopupWithdrawal, setIsOpenPopupWithdrawal] = useState(false);
  const [processingWithdrawal, setProcessingWithdrawal] = useState(null);
  const [isOpenCancelWithdrawal, setIsOpenCancelWithdrawal] = useState(false);
  const [loadingCancelWithdrawal, setLoadingCancelWithdrawal] = useState(false);
  const [loading, setLoading] = useState(true);

  const { headerVoucher } = useSelector((state) => state.voucher);

  const isShowHeaderBanner = headerVoucher && headerVoucher.length > 0;

  const handleChangeTab = (tab) => {
    setActiveTab(tab);
    setLoading(true);
    setPaging({ page: 0, total: 0 });
    setRowsPerPage(PAGINATION_LIMIT);
  };

  const closePopupWithdrawal = (_, reason) => {
    if (reason !== 'backdropClick') {
      setIsOpenPopupWithdrawal(false);
    }
  };

  const showPopupWithdrawal = (value) => {
    if (activeTab === TRANSACTION_HISTORY_TABS.WITHDRAWAL) {
      setProcessingWithdrawal(value);
      setIsOpenPopupWithdrawal(true);
    }
  };

  const offsetRequest = useMemo(
    () => paging?.page * rowsPerPage,
    [rowsPerPage, paging?.page],
  );

  const fetchTransactionDetail = async () => {
    if (transactionId) {
      const data = await apis.transactionsHistory.getWithdrawalRequestDetail(
        transactionId,
      );
      const voiceRevenueTransaction = data?.result?.voiceRevenueTransaction;
      if (voiceRevenueTransaction) {
        setProcessingWithdrawal(voiceRevenueTransaction);
        setIsOpenPopupWithdrawal(true);
      } else {
        history.push(ROUTES.HOME);
      }
    }
  };

  const fetchClaimRewards = async () => {
    setLoading(true);
    const data = await apis.rewardTransactions.getRewardTransactions({
      offset: offsetRequest,
      limit: rowsPerPage,
      sort: WITHDRAWAL_REQUEST_SORT.CREATED_AT_DESC,
    });
    if (data && data.status) {
      setHistoryRequests(data.result?.rewardTransactions);
      setPaging({ ...paging, total: data.result?.total });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (transactionId) {
      // If mobile, redirect to home page
      if (isMobile) history.push(ROUTES.HOME);
      else fetchTransactionDetail();
    }
  }, [transactionId, isMobile]);

  const fetchTransactions = async () => {
    setLoading(true);
    const data = await apis.transactionsHistory.getWithdrawalRequest({
      offset: offsetRequest,
      limit: rowsPerPage,
      sort: WITHDRAWAL_REQUEST_SORT.CREATED_AT_DESC,
    });
    if (data && data.status) {
      setHistoryRequests(data.result?.voiceRevenueTransactions);
      setPaging({ ...paging, total: data.result?.total });
    }
    setLoading(false);
  };

  const handleFetchTransactions = useCallback(async () => {
    if (activeTab === TRANSACTION_HISTORY_TABS.WITHDRAWAL) fetchTransactions();
    else fetchClaimRewards();
  }, [activeTab, offsetRequest, rowsPerPage, paging.page]);

  useEffect(() => {
    handleFetchTransactions();
  }, [rowsPerPage, offsetRequest, handleFetchTransactions]);

  const renderTypography = (content) => (
    <Typography variant="body-14-regular" color={COLOR.darkGray}>
      {content === 'null' || content === 'undefined' ? '' : content}
    </Typography>
  );

  const renderContentClaimRewardsTypography = (row, lang) => {
    const startDate = moment(row?.metadata?.startDate).format('DD/MM/YYYY');
    const endDate = moment(row?.metadata?.endDate).format('DD/MM/YYYY');
    const leaderboardType = row?.metadata?.leaderboardType;
    const rank = getOrdinal(row?.metadata?.rank, lang);
    return (
      <>
        <Typography variant="body-14-regular" color={COLOR.darkGray}>
          {t('rankRewardsDescription', {
            rank,
            leaderboardType: t(leaderboardType),
          })}
        </Typography>
        <br />
        <Typography variant="body-14-regular" color={COLOR.darkGray}>
          {startDate} - {endDate}
        </Typography>
      </>
    );
  };

  const renderCreditedStatus = () => {
    const color = COLOR.greenV2.default;
    const backgroundColor = COLOR.greenV2[8];
    return (
      <StyledChip
        color={color}
        backgroundColor={backgroundColor}
        sx={{ fontSize: '12px' }}
      >
        {t('credited')}
      </StyledChip>
    );
  };

  const renderMoneyTypography = (content) => (
    <Typography variant="body-14-regular" color={COLOR.darkGray}>
      <span className="vnd">đ</span>
      {content}
    </Typography>
  );
  const renderClaimRewardsAmountTypography = (content) => (
    <Typography variant="body-14-regular" color={COLOR.greenV2.default}>
      +<span className="vnd">đ</span>
      {content}
    </Typography>
  );

  const handleChangePage = (page) => {
    setPaging({ ...paging, page });
  };

  const formatDate = (date) => {
    if (date) return moment(date).format('DD-MM-YYYY HH:mm');
    return '-';
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPaging({ ...paging, page: 0 });
  };

  const getWithdrawalStatusText = (status) => {
    switch (status) {
      case VOICE_REVENUE_TRANSACTION_STATUS.PENDING:
      case VOICE_REVENUE_TRANSACTION_STATUS.INIT:
        return 'pendingV2';
      case VOICE_REVENUE_TRANSACTION_STATUS.CANCELLED:
        return 'canceled';
      case VOICE_REVENUE_TRANSACTION_STATUS.REJECTED:
        return 'rejected';
      case VOICE_REVENUE_TRANSACTION_STATUS.PROCESSING:
        return 'process';
      case VOICE_REVENUE_TRANSACTION_STATUS.PAID:
        return 'paid';
      default:
        return '';
    }
  };

  const closePopupCancelWithdrawal = () => {
    setIsOpenCancelWithdrawal(false);
  };

  const updateCancelWithdrawalRequests = (id) => {
    if (historyRequests?.length) {
      const newWithdrawalRequests = historyRequests?.map((request) => {
        if (request.id === id) {
          return {
            ...request,
            status: VOICE_REVENUE_TRANSACTION_STATUS.CANCELLED,
          };
        }
        return request;
      });
      setHistoryRequests(newWithdrawalRequests);
    }
  };

  const notify = (message, severity) => {
    dispatch(actions.noti.push({ severity, message }));
  };

  const handleCancelWithdrawalRequest = async () => {
    if (processingWithdrawal?.id) {
      setLoadingCancelWithdrawal(true);
      const response = await apis.transactionsHistory.cancelWithdrawalRequest(
        processingWithdrawal?.id,
      );
      const isSuccess = response.status;
      if (isSuccess) {
        notify(t('cancelWithdrawalRequestSuccess'), 'success');
        updateCancelWithdrawalRequests(processingWithdrawal?.id);
        setProcessingWithdrawal({
          ...processingWithdrawal,
          status: VOICE_REVENUE_TRANSACTION_STATUS.CANCELLED,
        });
      } else {
        const isInvalid = response?.code === VOICE_REVENUE_TRANSACTION_IN_VALID;
        const errorMessage = isInvalid
          ? t('cancellationWithdrawalRequestFailed')
          : t('errorOccurred');
        notify(errorMessage, 'error');
        fetchTransactions();
      }
      closePopupWithdrawal();
      closePopupCancelWithdrawal();
      setLoadingCancelWithdrawal(false);
    }
  };

  const renderWithdrawalStatus = (status) => {
    const color = VOICE_REVENUE_TRANSACTION_STATUS_TEXT_COLORS[status];
    const backgroundColor =
      VOICE_REVENUE_TRANSACTION_STATUS_BACKGROUND_COLORS[status];
    return (
      <StyledChip
        color={color}
        backgroundColor={backgroundColor}
        sx={{ fontSize: '12px' }}
      >
        {t(getWithdrawalStatusText(status))}
      </StyledChip>
    );
  };
  const getColumnsClaimRewards = () => [
    {
      title: t('claimRewardsDate'),
      align: 'center',
      field: 'createdAt',
      render: (row) => renderTypography(formatDate(row.createdAt)),
    },
    {
      title: t('rewardAmount'),
      align: 'center',
      field: 'amount',
      render: (row) =>
        renderClaimRewardsAmountTypography(
          delimitNumber(row?.money?.amount, language),
        ),
    },
    {
      title: t('content'),
      align: 'center',
      field: 'content',
      render: (row) => renderContentClaimRewardsTypography(row, language),
    },
    {
      title: t('status'),
      align: 'center',
      field: 'status',
      render: () => renderCreditedStatus(),
    },
  ];

  const getColumnsWithdrawal = () => [
    {
      title: t('fullNameV2'),
      align: 'center',
      field: 'user.fullName',
      render: (row) => renderTypography(row.user?.fullName),
    },
    {
      title: t('amountOfMoney'),
      align: 'center',
      field: 'amount',
      render: (row) =>
        renderMoneyTypography(delimitNumber(row.amount, language)),
    },
    {
      title: t('requestDate'),
      align: 'center',
      field: 'createdAt',
      render: (row) => renderTypography(formatDate(row.createdAt)),
    },
    {
      title: t('payoutDate'),
      align: 'center',
      field: 'paidAt',
      render: (row) => renderTypography(formatDate(row.paidAt)),
    },
    {
      title: t('content'),
      align: 'center',
      field: 'content',
      render: () => renderTypography(t('withdrawalV2')),
    },
    {
      title: t('status'),
      align: 'center',
      field: 'status',
      render: (row) => renderWithdrawalStatus(row.status),
    },
  ];

  const columns =
    activeTab === TRANSACTION_HISTORY_TABS.REWARD_TRANSACTION
      ? getColumnsClaimRewards()
      : getColumnsWithdrawal();

  return (
    <StyledTransactionsHistory isShowHeaderBanner={isShowHeaderBanner}>
      <Card flexDirection="column" className="transaction-history-card">
        <Box className="transaction-history-wrapper">
          <TransactionHistoryHeader />
          <Divider sx={{ margin: '24px' }} />
          <div className="transaction-history-table">
            <TransactionHistoryTabs
              activeTab={activeTab}
              handleChangeTab={handleChangeTab}
              tabs={TABS}
            />
            {loading ? (
              <div className="transaction-history-loading">
                <CircularProgress />
              </div>
            ) : (
              <>
                {historyRequests?.length ? (
                  <Table
                    columns={columns}
                    headerStyle={{ borderRadius: '16px 16px 0 0' }}
                    data={historyRequests}
                    page={paging.page}
                    total={paging.total}
                    rowsPerPage={rowsPerPage}
                    onChangeRowPage={handleChangeRowsPerPage}
                    onChangePage={handleChangePage}
                    rowsPerPageOptions={[5, 10, 25]}
                    onRowClick={showPopupWithdrawal}
                    styleFooter={{
                      padding: '16px 16px 16px 0px',
                      height: 'auto',
                    }}
                    isRowClickable={
                      activeTab === TRANSACTION_HISTORY_TABS.WITHDRAWAL
                    }
                  />
                ) : (
                  <EmptyWithdrawalRequest description="noData" isDescription />
                )}
              </>
            )}
          </div>
        </Box>
      </Card>
      <Withdrawal
        isOpenPopupWithdrawal={isOpenPopupWithdrawal}
        closePopupWithdrawal={closePopupWithdrawal}
        key={isOpenPopupWithdrawal}
        processingWithdrawal={processingWithdrawal}
        isShowDetail
        onCancelWithdrawal={() => setIsOpenCancelWithdrawal(true)}
      />
      <CancelWithdrawalRequestDialog
        open={isOpenCancelWithdrawal}
        onClose={closePopupCancelWithdrawal}
        withdrawalRequestId={processingWithdrawal?.id}
        onCancelWithdrawalRequest={handleCancelWithdrawalRequest}
        loading={loadingCancelWithdrawal}
      />
    </StyledTransactionsHistory>
  );
};

export default TransactionsHistory;
